import { useCallback } from "react";
import { useProperty } from "../../../../components/property-provider";
import { useBackofficeClient } from "../../../../lib/http";
import { getDataByActions, Image } from "../../../shared/widgets/ImageManager";
import { transformMultimediaToImageManageObject } from "../mappers";
import { CustomExtra, FormType } from "../types";

export function useMultimedia() {
  const property = useProperty();
  const httpClient = useBackofficeClient();

  const fetchCreateImages = useCallback(
    async (formValues: FormType, images: Image[]) => {
      const blobImages = images.filter(({ url }) => url.startsWith("blob:"));
      if (!images || images.length === 0 || blobImages.length === 0) return;
      const formData = new FormData();
      blobImages.forEach((image: Image) => {
        formData.append("images", image, image.id);
        formData.append(
          "medias",
          new Blob(
            [
              JSON.stringify({
                id: image.id,
                filename: image.filename.toLowerCase(),
                translations:
                  image.translations && transformMultimediaToImageManageObject(image.translations),
              }),
            ],
            {
              type: "application/json",
            }
          )
        );
      });

      await httpClient.post<any, any>(
        `/v1/${property.ticker}/extra/${formValues.id}/media/multi`,
        formData
      );
    },
    [httpClient, property.ticker]
  );

  const fetchUpdateImages = useCallback(
    async (extraId: string, images: Image[]) => {
      if (images.length) {
        const request = images.map((it: Image) => ({
          ...it,
          translations: it.translations && transformMultimediaToImageManageObject(it.translations),
        }));
        await httpClient.put(`/v1/${property.ticker}/extra/${extraId}/media`, request);
      }
    },
    [httpClient, property.ticker]
  );

  const fetchRemoveImages = useCallback(
    async (extraId: string, images: Image[]) => {
      if (images.length) {
        await Promise.all(
          images.map(it =>
            httpClient.delete(`/v1/${property.ticker}/extra/${extraId}/media/${it.id}`)
          )
        );
      }
    },
    [httpClient, property.ticker]
  );

  const handleUploadImages = useCallback(
    async (formValues: FormType, multimediaList: Image[]) => {
      return await fetchCreateImages(formValues, multimediaList);
    },
    [fetchCreateImages]
  );

  const handleUpdateImages = useCallback(
    async (extra: CustomExtra, formValues: FormType, multimediaList: Image[]) => {
      const { create, update, remove } = getDataByActions(extra.multimediaList, multimediaList);
      return await Promise.all([
        fetchCreateImages({ ...formValues, id: extra.id }, create),
        fetchRemoveImages(extra.id, remove),
        fetchUpdateImages(extra.id, update),
      ]);
    },
    [fetchCreateImages, fetchRemoveImages, fetchUpdateImages]
  );

  return { handleUploadImages, handleUpdateImages };
}
